/**
 * The API client.
 *
 * The only module in the browser that touches the network, which is the point:
 * a reviewer can confirm what leaves the machine by reading one file. It sends
 * a part count and, for a report, a table of derived numbers. It never sees the
 * DXF — the file is read inside a worker that imports the engine and nothing
 * else.
 */

export type Plan = "free" | "day" | "pro";

export interface Limits {
  readonly maxParts: number | null;
  readonly mayExport: boolean;
  readonly mayRemapLayers: boolean;
  readonly mayUseNamingTemplates: boolean;
  readonly mayGenerateReport: boolean;
  readonly maySaveProfiles: boolean;
  /** Export several queued nests together as one deduplicated ZIP. Pro only. */
  readonly mayDeduplicateExport: boolean;
}

export interface Session {
  readonly signedIn: boolean;
  /** Set on a fresh registration: the account exists but cannot sign in until its email is verified. */
  readonly pendingVerification?: boolean;
  readonly email?: string;
  readonly firstName?: string;
  readonly lastName?: string;
  readonly company?: string | null;
  readonly plan?: Plan;
  readonly planExpiresAt?: string | null;
  readonly planStartedAt?: string | null;
  readonly limits?: Limits;
  readonly isAdmin?: boolean;
}

export interface RedemptionGrant {
  readonly plan: Plan;
  readonly planExpiresAt: string;
}

export interface AdminAccount {
  readonly id: number;
  readonly email: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly company: string | null;
  readonly plan: Plan;
  readonly planExpiresAt: string | null;
  readonly isAdmin: boolean;
  readonly nestsSplit: number;
  readonly partsSplit: number;
  readonly lastSplitAt: string | null;
}

export interface AdminRedemptionCode {
  readonly email: string;
  readonly plan: Plan;
  readonly days: number;
  readonly amountCents: number | null;
  readonly currency: string;
  readonly issuedAt: string;
  readonly expiresAt: string;
  readonly redeemedAt: string | null;
}

export interface PurchaseRecord {
  readonly id: number;
  readonly purchasedAt: string;
  readonly plan: Plan;
  readonly periodDays: number;
  readonly amountCents: number | null;
  readonly currency: string;
  readonly method: "redemption_code" | "card";
}

export interface Grant {
  readonly token: string;
  readonly plan: Plan;
  readonly expiresAt: string;
  readonly limits: Limits;
  readonly notice: string | null;
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    /** Machine-readable reason, e.g. "session_superseded". Not every error carries one. */
    readonly code?: string,
  ) {
    super(message);
  }
}

/**
 * Where the API lives, relative to where the app is served from.
 *
 * The app sits at the site root in production and in a folder on a development
 * machine, and the API is always its sibling — `/api` in the first case,
 * `/dxfsplitter/api` in the second. Deriving it from the document's own base
 * URL means one build works in both, and means no absolute origin is compiled
 * into the bundle that could later point somewhere unintended.
 */
function defaultBase(): string {
  const path = new URL(document.baseURI).pathname;
  // The app is served from `<mount>/app/`; the API is `<mount>/api`.
  const mount = path.replace(/\/app\/?$/, "/").replace(/\/[^/]*$/, "/");
  return (mount === "/" ? "" : mount.replace(/\/$/, "")) + "/api";
}

export class Api {
  /**
   * The CSRF token, refreshed from every response that carries one.
   *
   * Held in memory only. Putting it in storage would outlive the session it
   * belongs to and be readable by any script on the origin.
   */
  private csrf = "";

  /**
   * Fired once, right before any request throws an `ApiError` whose `code`
   * is "session_superseded" — i.e. the server has just told us this browser
   * was signed out because the account logged in elsewhere. Wired up in one
   * place (main.ts) so every call site gets the same "you were logged out
   * elsewhere" handling without each one needing its own 401 branch.
   */
  onSessionSuperseded: (() => void) | null = null;

  constructor(private readonly base = defaultBase()) {}

  async session(): Promise<Session> {
    return this.request<Session>("GET", "/auth/session");
  }

  async register(
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    company: string,
  ): Promise<Session> {
    return this.request<Session>("POST", "/auth/register", {
      email,
      password,
      firstName,
      lastName,
      company,
    });
  }

  async login(email: string, password: string): Promise<Session> {
    return this.request<Session>("POST", "/auth/login", { email, password });
  }

  async logout(): Promise<void> {
    await this.request("POST", "/auth/logout", {});
  }

  /**
   * Ask for a password-reset email. Always resolves the same way whether or
   * not the address is registered — see AuthService::requestPasswordReset().
   */
  async forgotPassword(email: string): Promise<{ sent: boolean }> {
    return this.request("POST", "/auth/forgot-password", { email });
  }

  /**
   * Ask for permission to export.
   *
   * `partCount` is what detection found. It is sent so the server can warn a
   * free user that their nest will be truncated, and so unusual volume on an
   * account is visible. It does not decide anything: the server resolves the
   * plan from the account, so falsifying this number changes nothing except the
   * accuracy of the message that comes back.
   *
   * `dedupe` is different: it says this token is for a combined,
   * deduplicated export across several queued nests, and the server checks it
   * against the plan (Pro only) before issuing. That check is the only
   * enforcement this feature gets — the worker that later spends the token
   * runs on the user's machine and cannot verify it — so unlike `partCount`,
   * falsifying this one does matter, which is exactly why the server, not
   * this client, decides what it means.
   */
  async entitlement(partCount: number, dedupe = false): Promise<Grant> {
    return this.request<Grant>("POST", "/entitlement", { partCount, dedupe });
  }

  /**
   * Report that a freshly-loaded file was split, for the admin panel's usage
   * counters. Fire-and-forget from the caller's perspective — a failure here
   * must never block or surface to the user, since it is bookkeeping, not a
   * gate.
   */
  async recordSplit(partCount: number): Promise<{ ok: boolean }> {
    return this.request("POST", "/usage/record-split", { partCount });
  }

  /** Redeem an unlock code against the signed-in account. */
  async redeem(code: string): Promise<RedemptionGrant> {
    return this.request<RedemptionGrant>("POST", "/redeem", { code });
  }

  /** Update the signed-in account's name and company. Email is never editable here. */
  async updateProfile(firstName: string, lastName: string, company: string): Promise<Session> {
    return this.request<Session>("POST", "/account/profile", { firstName, lastName, company });
  }

  /** Change the signed-in account's password, given the current one. */
  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await this.request("POST", "/account/password", { currentPassword, newPassword });
  }

  /** The signed-in account's purchase history, most recent first. */
  async purchases(): Promise<{ purchases: PurchaseRecord[] }> {
    return this.request("GET", "/account/purchases");
  }

  /**
   * Download one purchase's invoice as a PDF.
   *
   * Bypasses `request()`, which always parses the response as JSON — a PDF
   * needs the raw bytes instead. Same pattern as the parts report in
   * `pro/index.ts`.
   */
  async downloadInvoice(purchaseId: number): Promise<Blob> {
    const response = await fetch(this.base + "/account/purchases/invoice", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(this.csrf !== "" ? { "X-CSRF-Token": this.csrf } : {}),
      },
      credentials: "same-origin",
      body: JSON.stringify({ purchaseId }),
    });

    if (!response.ok) {
      let code: string | undefined;
      try {
        const json: unknown = await response.clone().json();
        if (json !== null && typeof json === "object" && "code" in json) {
          code = String((json as { code: unknown }).code);
        }
      } catch {
        // Not JSON (or empty) — fine, code just stays undefined.
      }
      if (code === "session_superseded") this.onSessionSuperseded?.();
      throw new ApiError("The invoice could not be downloaded.", response.status, code);
    }

    return response.blob();
  }

  readonly admin = {
    listAccounts: (): Promise<{ accounts: AdminAccount[] }> =>
      this.request("GET", "/admin/accounts"),

    listCodes: (): Promise<{ codes: AdminRedemptionCode[] }> =>
      this.request("GET", "/admin/redemption-codes"),

    generateCode: (
      email: string,
      plan: "day" | "pro",
      days: number,
      amountCents?: number,
      sendEmail?: boolean,
      planName?: string,
    ): Promise<{ code: string }> =>
      this.request("POST", "/admin/redemption-codes", { email, plan, days, amountCents, sendEmail, planName }),

    updateAccountProfile: (
      accountId: number,
      firstName: string,
      lastName: string,
      company: string,
    ): Promise<AdminAccount> =>
      this.request("POST", "/admin/accounts/update-profile", { accountId, firstName, lastName, company }),
  };

  private async request<T>(
    method: string,
    path: string,
    body?: Record<string, unknown>,
  ): Promise<T> {
    // A POST always carries a body, even an empty one. IIS answers a POST with
    // no Content-Length with 411 rather than routing it, which broke sign-out
    // when the app was first served from IIS rather than a dev server.
    const payload = body ?? (method === "POST" ? {} : undefined);

    const headers: Record<string, string> = { Accept: "application/json" };
    if (payload !== undefined) headers["Content-Type"] = "application/json";
    if (this.csrf !== "") headers["X-CSRF-Token"] = this.csrf;

    const response = await fetch(this.base + path, {
      method,
      headers,
      // The session is a cookie, so it must ride along; `same-origin` rather
      // than `include` because there is no cross-origin case to support.
      credentials: "same-origin",
      ...(payload !== undefined ? { body: JSON.stringify(payload) } : {}),
    });

    const text = await response.text();
    let json: unknown = null;
    if (text !== "") {
      try {
        json = JSON.parse(text);
      } catch {
        json = null;
      }
    }

    if (json !== null && typeof json === "object" && "csrfToken" in json) {
      const token = (json as { csrfToken: unknown }).csrfToken;
      if (typeof token === "string") this.csrf = token;
    }

    if (!response.ok) {
      const message =
        json !== null && typeof json === "object" && "error" in json
          ? String((json as { error: unknown }).error)
          : "Something went wrong. Please try again.";
      const code =
        json !== null && typeof json === "object" && "code" in json
          ? String((json as { code: unknown }).code)
          : undefined;
      if (code === "session_superseded") this.onSessionSuperseded?.();
      throw new ApiError(message, response.status, code);
    }

    return json as T;
  }
}
